import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, X, AlertTriangle } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const TYPE_LABELS = { entry: "Entrada", exit: "Sortida", pause_start: "Inici pausa", pause_end: "Fi pausa" };

async function logAudit(venueId, recordId, action, performedBy, reason, previousValue, newValue, workerEmail, workerName) {
  await hosys.entities.TimeAuditLog.create({
    venue_id: venueId,
    record_id: recordId,
    action,
    performed_by_email: performedBy.email,
    performed_by_name: performedBy.full_name || performedBy.email,
    performed_at: new Date().toISOString(),
    reason,
    previous_value: previousValue ? JSON.stringify(previousValue) : null,
    new_value: newValue ? JSON.stringify(newValue) : null,
    worker_email: workerEmail,
    worker_name: workerName,
  });
}

export default function EditDayModalLegal({ member, date, records, onClose, onSaved, venueId, currentUser }) {
  const [rows, setRows] = useState(() =>
    records.map(r => ({
      id: r.id,
      type: r.type,
      time: moment(r.timestamp).format("HH:mm"),
      originalType: r.type,
      originalTime: moment(r.timestamp).format("HH:mm"),
      _delete: false,
    }))
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [reasonError, setReasonError] = useState(false);

  const addRow = () => setRows(r => [...r, { id: null, type: "entry", time: "08:00", _delete: false }]);
  const removeRow = (i) => setRows(r => r.map((row, idx) => idx === i ? { ...row, _delete: true } : row));
  const updateRow = (i, field, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const hasChanges = () => {
    const toDelete = rows.filter(r => r.id && r._delete);
    const toCreate = rows.filter(r => !r.id && !r._delete);
    const toUpdate = rows.filter(r => r.id && !r._delete && (r.type !== r.originalType || r.time !== r.originalTime));
    return toDelete.length > 0 || toCreate.length > 0 || toUpdate.length > 0;
  };

  const save = async () => {
    if (hasChanges() && !reason.trim()) {
      setReasonError(true);
      return;
    }
    setSaving(true);

    const now = new Date().toISOString();
    const workerEmail = member.user_email;
    const workerName = member.user_name || member.user_email;

    const toDelete = rows.filter(r => r.id && r._delete);
    const toCreate = rows.filter(r => !r.id && !r._delete);
    const toUpdate = rows.filter(r => r.id && !r._delete && (r.type !== r.originalType || r.time !== r.originalTime));

    // Soft-delete (never physically delete records)
    await Promise.all(toDelete.map(async (r) => {
      const prev = records.find(rec => rec.id === r.id);
      await hosys.entities.TimeRecord.update(r.id, {
        is_deleted: true,
        deleted_by: currentUser.email,
        deleted_at: now,
        deletion_reason: reason,
      });
      await logAudit(venueId, r.id, "delete", currentUser, reason, prev, null, workerEmail, workerName);
    }));

    // Create new records
    await Promise.all(toCreate.map(async (r) => {
      const newRec = await hosys.entities.TimeRecord.create({
        venue_id: venueId,
        user_email: workerEmail,
        user_name: workerName,
        type: r.type,
        timestamp: moment(`${date}T${r.time}`).toISOString(),
        date,
        source: "manual",
        last_edited_by: currentUser.email,
        last_edited_at: now,
        edit_reason: reason,
      });
      await logAudit(venueId, newRec.id, "create", currentUser, reason, null, { type: r.type, time: r.time }, workerEmail, workerName);
    }));

    // Update existing records
    await Promise.all(toUpdate.map(async (r) => {
      const prev = records.find(rec => rec.id === r.id);
      await hosys.entities.TimeRecord.update(r.id, {
        type: r.type,
        timestamp: moment(`${date}T${r.time}`).toISOString(),
        last_edited_by: currentUser.email,
        last_edited_at: now,
        edit_reason: reason,
      });
      await logAudit(venueId, r.id, "edit", currentUser, reason,
        { type: prev?.type, time: prev ? moment(prev.timestamp).format("HH:mm") : null },
        { type: r.type, time: r.time },
        workerEmail, workerName
      );
    }));

    setSaving(false);
    onSaved();
  };

  const visible = rows.filter(r => !r._delete);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900">{member.user_name || member.user_email}</h3>
            <p className="text-sm text-gray-400 capitalize">{moment(date).format("dddd, D MMMM YYYY")}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-2 mb-4">
          {visible.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Sense registres. Afegeix-ne.</p>}
          {visible.map((row, i) => {
            const realIdx = rows.indexOf(row);
            return (
              <div key={`row-${i}`} className="flex items-center gap-2">
                <select
                  value={row.type}
                  onChange={e => updateRow(realIdx, "type", e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <Input
                  type="time"
                  value={row.time}
                  onChange={e => updateRow(realIdx, "time", e.target.value)}
                  className="w-28"
                />
                <button onClick={() => removeRow(realIdx)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        <button onClick={addRow} className="w-full border-2 border-dashed border-gray-200 rounded-xl py-2 text-sm text-gray-400 hover:border-[#7B2D3F] hover:text-[#7B2D3F] transition-colors flex items-center justify-center gap-1 mb-4">
          <Plus className="w-4 h-4" /> Afegir registre
        </button>

        {/* Legal audit field */}
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            Motiu de la modificació <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Descriu el motiu de l'edició (obligatori per normativa)"
            value={reason}
            onChange={e => { setReason(e.target.value); setReasonError(false); }}
            className={reasonError ? "border-red-400 focus:ring-red-400" : ""}
          />
          {reasonError && <p className="text-xs text-red-500 mt-1">El motiu és obligatori per complir la normativa de registre horari.</p>}
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4">
          <p className="text-xs text-amber-700">
            <strong>Avís legal:</strong> Totes les modificacions queden registrades amb data, hora i usuari responsable d'acord amb el RD-Llei 8/2019.
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white">
            {saving ? "Guardant..." : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}